import { useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, Clock, Copy, Download, FileJson } from "lucide-react";
import { AGENT_DISPLAY_NAMES } from "@/lib/agent-ids";
import { formatDateTime } from "@/lib/datetime";
import type { Artifact } from "@/types/api";

interface ArtifactViewerProps {
  artifact: Artifact | null;
  onClose: () => void;
}

type ParsedShape =
  | { kind: "json-array"; rows: Record<string, unknown>[] }
  | { kind: "json-object"; obj: Record<string, unknown> }
  | { kind: "json-other"; value: unknown }
  | { kind: "text"; text: string }
  | { kind: "empty" };

function parseArtifactContent(content: unknown): ParsedShape {
  if (content == null) return { kind: "empty" };

  // Backend may serialize content as raw JSON OR pass the structured object
  // through directly (the Artifact type says `string | null` but in practice
  // some endpoints return parsed objects/arrays). Handle both.
  if (Array.isArray(content)) {
    if (
      content.length > 0 &&
      content.every((it) => it !== null && typeof it === "object")
    ) {
      return { kind: "json-array", rows: content as Record<string, unknown>[] };
    }
    return { kind: "json-other", value: content };
  }
  if (typeof content === "object") {
    return { kind: "json-object", obj: content as Record<string, unknown> };
  }
  if (typeof content !== "string") {
    return { kind: "json-other", value: content };
  }

  const trimmed = content.trim();
  if (!trimmed) return { kind: "empty" };
  // Quick JSON sniff — try to parse only when it starts with { or [.
  if (trimmed[0] === "{" || trimmed[0] === "[") {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        if (
          parsed.length > 0 &&
          parsed.every((it) => it !== null && typeof it === "object")
        ) {
          return { kind: "json-array", rows: parsed as Record<string, unknown>[] };
        }
        return { kind: "json-other", value: parsed };
      }
      if (parsed !== null && typeof parsed === "object") {
        return { kind: "json-object", obj: parsed as Record<string, unknown> };
      }
      return { kind: "json-other", value: parsed };
    } catch {
      // fallthrough — not valid JSON
    }
  }
  return { kind: "text", text: content };
}

export function ArtifactViewer({ artifact, onClose }: ArtifactViewerProps) {
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(
    () => parseArtifactContent(artifact?.content),
    [artifact?.content],
  );

  if (!artifact) return null;

  const agentName = AGENT_DISPLAY_NAMES[artifact.agent_id] || artifact.agent_id;
  const formattedTime = formatDateTime(artifact.created_at);

  const rawJson = (() => {
    if (parsed.kind === "empty") return "";
    if (parsed.kind === "text") return parsed.text;
    const value =
      parsed.kind === "json-array"
        ? parsed.rows
        : parsed.kind === "json-object"
          ? parsed.obj
          : parsed.value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  })();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawJson);
      setCopied(true);
      toast.success("Content copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const handleDownload = () => {
    const isJsonShape = parsed.kind.startsWith("json");
    const blob = new Blob([rawJson], {
      type: isJsonShape ? "application/json" : "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = artifact.title.replace(/[^A-Za-z0-9_-]+/g, "_");
    a.download = `${safeName}.${isJsonShape ? "json" : "txt"}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = buildSummary(parsed);
  const hasStructuredPreview =
    parsed.kind === "json-array" || parsed.kind === "json-object";

  return (
    <Sheet open={!!artifact} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto flex flex-col">
        <SheetHeader className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <SheetTitle className="text-base">{artifact.title}</SheetTitle>
            <Badge variant="outline" className="text-[10px] font-mono">
              {artifact.artifact_type}
            </Badge>
          </div>
          <SheetDescription className="text-xs flex items-center gap-2 flex-wrap">
            <span>Generated by</span>
            <Badge variant="outline" className="text-[10px]">
              {agentName}
            </Badge>
            <span className="text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formattedTime}
            </span>
          </SheetDescription>
        </SheetHeader>

        {summary && (
          <div className="mt-3 rounded-md border bg-muted/30 px-3 py-2 text-xs flex flex-wrap gap-x-4 gap-y-1">
            {summary.map((item) => (
              <span key={item.label}>
                <span className="text-muted-foreground">{item.label}:</span>{" "}
                <strong className="text-foreground tabular-nums">
                  {item.value}
                </strong>
              </span>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={handleCopy}
          >
            {copied ? (
              <>
                <Check className="h-3 w-3" /> Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" /> Copy
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={handleDownload}
          >
            <Download className="h-3 w-3" /> Download
          </Button>
        </div>

        <div className="mt-4 flex-1 min-h-0">
          {parsed.kind === "empty" ? (
            <EmptyState />
          ) : hasStructuredPreview ? (
            <Tabs defaultValue="preview" className="space-y-3">
              <TabsList className="grid grid-cols-2 w-fit">
                <TabsTrigger value="preview" className="text-xs">
                  View
                </TabsTrigger>
                <TabsTrigger value="raw" className="text-xs gap-1">
                  <FileJson className="h-3 w-3" />
                  JSON
                </TabsTrigger>
              </TabsList>
              <TabsContent value="preview" className="mt-0">
                <StructuredPreview parsed={parsed} />
              </TabsContent>
              <TabsContent value="raw" className="mt-0">
                <RawJson content={rawJson} />
              </TabsContent>
            </Tabs>
          ) : parsed.kind === "text" ? (
            <TextContent text={parsed.text} />
          ) : (
            <RawJson content={rawJson} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
      Artifact with no persisted content.
    </div>
  );
}

function TextContent({ text }: { text: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3 max-h-[60vh] overflow-auto">
      <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed">
        {text}
      </pre>
    </div>
  );
}

function RawJson({ content }: { content: string }) {
  return (
    <div className="rounded-md border bg-[#0f172a] text-slate-100 p-3 max-h-[60vh] overflow-auto">
      <pre className="text-[11px] font-mono whitespace-pre leading-relaxed">
        {content}
      </pre>
    </div>
  );
}

function StructuredPreview({
  parsed,
}: {
  parsed: Extract<ParsedShape, { kind: "json-array" } | { kind: "json-object" }>;
}) {
  if (parsed.kind === "json-array") {
    return <ArrayTable rows={parsed.rows} />;
  }
  return <ObjectView obj={parsed.obj} />;
}

const MAX_ROWS_PREVIEW = 25;
const MAX_COLS_PREVIEW = 6;

function ArrayTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (rows.length === 0) {
    return <EmptyState />;
  }
  // Column discovery — first 6 keys observed across the first 20 rows
  const cols = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows.slice(0, 20)) {
      for (const k of Object.keys(r)) {
        if (!seen.has(k)) seen.add(k);
        if (seen.size >= MAX_COLS_PREVIEW) break;
      }
      if (seen.size >= MAX_COLS_PREVIEW) break;
    }
    return Array.from(seen);
  }, [rows]);

  const visibleRows = rows.slice(0, MAX_ROWS_PREVIEW);
  const truncated = rows.length - visibleRows.length;

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-muted-foreground">
        Showing {visibleRows.length} of {rows.length} entry(ies) ·{" "}
        {cols.length} column(s) ·{" "}
        {Object.keys(rows[0]).length > cols.length &&
          `(${Object.keys(rows[0]).length - cols.length} extra field(s) in the full view)`}
      </p>
      <div className="rounded-md border max-h-[55vh] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {cols.map((c) => (
                <TableHead key={c} className="text-[10px] uppercase tracking-wider">
                  {c}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row, i) => (
              <TableRow key={i}>
                {cols.map((c) => (
                  <TableCell key={c} className="text-xs font-mono">
                    {formatCell(row[c])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {truncated > 0 && (
        <p className="text-[11px] text-muted-foreground italic">
          + {truncated} hidden entry(ies). Use the "JSON" tab or download to
          see everything.
        </p>
      )}
    </div>
  );
}

function ObjectView({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj);
  return (
    <div className="rounded-md border max-h-[60vh] overflow-auto">
      <dl className="divide-y">
        {entries.map(([key, value]) => (
          <div
            key={key}
            className="grid grid-cols-[160px,1fr] gap-3 px-3 py-2 text-xs"
          >
            <dt className="text-muted-foreground font-mono break-all">{key}</dt>
            <dd className="font-mono break-all">{formatCell(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null) return "—";
  if (v === undefined) return "—";
  if (typeof v === "string") {
    return v.length > 80 ? v.slice(0, 77) + "…" : v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    return `[${v.length} item${v.length === 1 ? "" : "s"}]`;
  }
  if (typeof v === "object") {
    const keys = Object.keys(v as Record<string, unknown>);
    return `{${keys.length} field${keys.length === 1 ? "" : "s"}}`;
  }
  return String(v);
}

// ---------------------------------------------------------------------------
// Summary header — quick stats at the top
// ---------------------------------------------------------------------------

interface SummaryItem {
  label: string;
  value: string;
}

function buildSummary(parsed: ParsedShape): SummaryItem[] | null {
  if (parsed.kind === "json-array") {
    const n = parsed.rows.length;
    if (n === 0) return null;
    const fields = parsed.rows[0]
      ? Object.keys(parsed.rows[0]).length
      : 0;
    return [
      { label: "Records", value: n.toLocaleString("pt-BR") },
      { label: "Fields", value: fields.toString() },
    ];
  }
  if (parsed.kind === "json-object") {
    const items: SummaryItem[] = [];
    // Pick out a few well-known collection keys for a quick "what's in here"
    const knownCollections = [
      "users",
      "scope_items",
      "bom_line_items",
      "wricef_items",
      "gaps",
      "decisions",
      "transactions",
      "modules",
      "abap_objects",
    ];
    for (const key of knownCollections) {
      const v = parsed.obj[key];
      if (Array.isArray(v)) {
        items.push({
          label: key.replace(/_/g, " "),
          value: v.length.toLocaleString("pt-BR"),
        });
      }
    }
    // Fallback: total keys at the root
    if (items.length === 0) {
      items.push({
        label: "Fields",
        value: Object.keys(parsed.obj).length.toString(),
      });
    }
    return items;
  }
  return null;
}
